import { useFormContext } from "react-hook-form";

import Box from "@/components/Box";
import Loading from "@/components/Loading";
import {
  DOCUMENT_FILE_ACCEPT,
  DOCUMENT_FILE_ACCEPT_HELP_TEXT,
  FileInputWrapper,
  UPLOADING_STATUS,
  useUploadFileToS3
} from "@/components/UploadFileToS3";
import FileIcon from "@/images/file-icon.svg?react";
import ControlledFormField from "@/tpo/ControlledFormField";
import DateInput from "@/tpo/DateInput";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Group from "@/tpo/Group";
import InputWrapper from "@/tpo/InputWrapper";
import Stack from "@/tpo/Stack";
import { buildPartialDateValue } from "@/tpo/partnerDashboard/practitioner/Application/buildPractitionerApplicationMutationInput";
import ButtonV2 from "@/v2/Buttons";

const DOCUMENT_INSURANCE_TYPE = "Insurance";

function InsuranceEvidenceUpload() {
  const { getValues, setValue, watch, getFieldState } = useFormContext();
  const insuranceEvidence = watch("insuranceEvidence");

  const { status, upload, errorMessage } = useUploadFileToS3({
    accept: DOCUMENT_FILE_ACCEPT,
    onChange: ({ key, file }) => {
      const evidence = { key, name: file.name };
      const currentDocuments = getValues("documents") || [];
      setValue("insuranceEvidence", evidence, { shouldDirty: true, shouldValidate: true });
      setValue(
        "documents",
        [
          ...currentDocuments.filter(doc => doc.type !== DOCUMENT_INSURANCE_TYPE),
          { ...evidence, type: DOCUMENT_INSURANCE_TYPE, qualification_type: "" }
        ],
        { shouldDirty: true, shouldValidate: true }
      );
    }
  });

  const { error } = getFieldState("insuranceEvidence");

  return (
    <Stack gap={10}>
      <Box fontFamily="gilroyMedium" fontSize={16}>
        Upload evidence of your current professional insurance
      </Box>
      <InputWrapper
        error={error?.message}
        input={
          <Group
            alignItems="center"
            justifyContent="space-between"
            gap={20}
            flexWrap="wrap"
            maxWidth="100%"
          >
            <Group
              gap={20}
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden"
              }}
              alignItems="center"
            >
              {status === UPLOADING_STATUS ? (
                <Loading minHeight={40} minWidth={40} size={20} />
              ) : (
                <FileIcon />
              )}
              <Box
                fontFamily="gilroyBold"
                fontSize={12}
                letterSpacing="2.8px"
                textTransform="uppercase"
              >
                {insuranceEvidence?.name || "Add a file"}
              </Box>
            </Group>
            {!insuranceEvidence ? (
              <FileInputWrapper>
                <input
                  type="file"
                  accept={DOCUMENT_FILE_ACCEPT}
                  onChange={upload}
                  disabled={status === UPLOADING_STATUS}
                />
                <ButtonV2
                  color="green"
                  type="button"
                  data-testid="addInsuranceEvidenceButton"
                  size="sm"
                >
                  upload
                </ButtonV2>
              </FileInputWrapper>
            ) : (
              <ButtonV2
                color="red"
                size="sm"
                type="button"
                onClick={() => {
                  setValue("insuranceEvidence", null, { shouldDirty: true, shouldValidate: true });
                  const currentDocuments = getValues("documents") || [];
                  setValue(
                    "documents",
                    currentDocuments.filter(doc => doc.type !== DOCUMENT_INSURANCE_TYPE),
                    { shouldDirty: true, shouldValidate: true }
                  );
                }}
                data-testid="removeInsuranceEvidenceButton"
              >
                remove
              </ButtonV2>
            )}
          </Group>
        }
      />
      <Box fontFamily="gilroyMedium" fontSize={[12, 14, 14]} color="#818181" lineHeight="20px">
        {DOCUMENT_FILE_ACCEPT_HELP_TEXT}
      </Box>
      {errorMessage && (
        <Box fontFamily="gilroyMedium" fontSize={12} color="red" lineHeight="20px">
          {errorMessage}
        </Box>
      )}
    </Stack>
  );
}

export default function ProfessionalVerification() {
  return (
    <Stack gap={40}>
      <ControlledFormField
        name="insuranceProvider"
        label="Insurance provider"
        Component={FloatingLabelInput}
      />
      <ControlledFormField
        name="insurancePolicyNumber"
        label="Policy number"
        Component={FloatingLabelInput}
      />
      <ControlledFormField
        name="insuranceExpiryDate"
        Component={DateInput}
        getDay={dateStr => (dateStr ? dateStr.split("-")[2] : "")}
        getMonth={dateStr => (dateStr ? dateStr.split("-")[1] : "")}
        getYear={dateStr => (dateStr ? dateStr.split("-")[0] : "")}
        constructDate={buildPartialDateValue}
        label="Expiry date"
        labelProps={{
          fontFamily: "gilroyBold",
          fontSize: 18,
          mb: 20
        }}
      />
      <InsuranceEvidenceUpload />
    </Stack>
  );
}
